import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Autocomplete, TextField, Typography } from '@material-ui/core'
import { useGeneSearch } from 'src/api/unified/geneSearch'
import { computeBoundsForGene } from 'src/api/variant/geneSpan'
import GeneIgvViewer from 'src/components/GeneIgv/GeneIgvViewer'

const optionLabel = (o) => (typeof o === 'string' ? o : o?.gene_symbol || '')

// IGV gene browser: the user searches a gene, we resolve its coordinates
// (GENCODE) and open igv.js bounded to the gene span, overlaying the patient's
// BAM reads, SNVs, and CNV/SV. Rendered in the IGV dialog on Home.
const GeneBrowserTab = ({ fileId, analysisWrapperId, inlineHeight }) => {
  const [inputValue, setInputValue] = useState('')
  const [selectedGene, setSelectedGene] = useState(null)

  const { data, isFetching } = useGeneSearch(inputValue, { limit: 10 })
  const options = data?.genes || []

  const region = useMemo(() => {
    if (!selectedGene) return null
    const { start, end } = computeBoundsForGene({ start: selectedGene.start, end: selectedGene.end })
    return { chrom: selectedGene.chrom, start, end }
  }, [selectedGene])

  const selector = (
    <Autocomplete
      fullWidth
      options={options}
      loading={isFetching}
      value={selectedGene}
      inputValue={inputValue}
      // Server-side prefix search already ranks results — keep them all.
      filterOptions={(x) => x}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(o, v) => o?.gene_symbol === v?.gene_symbol && o?.chrom === v?.chrom}
      onInputChange={(_e, v) => setInputValue(v)}
      onChange={(_e, v) => setSelectedGene(v)}
      renderOption={(props, o) => (
        <li {...props} key={`${o.gene_symbol}-${o.chrom}-${o.start}`}>
          <Typography variant="body2">
            {o.gene_symbol}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {o.chrom}:{o.start?.toLocaleString()}-{o.end?.toLocaleString()}
            </Typography>
          </Typography>
        </li>
      )}
      renderInput={(params) => <TextField {...params} label="Gene" variant="outlined" size="small" />}
    />
  )

  return (
    <GeneIgvViewer
      region={region}
      geneSymbol={selectedGene?.gene_symbol || null}
      fileId={fileId}
      analysisWrapperId={analysisWrapperId}
      selector={selector}
      title={selectedGene ? `IGV Gene Browser · ${selectedGene.gene_symbol}` : 'IGV Gene Browser'}
      emptyHint="Search for a gene to open the browser."
      inlineHeight={inlineHeight}
    />
  )
}

GeneBrowserTab.propTypes = {
  fileId: PropTypes.string,
  analysisWrapperId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inlineHeight: PropTypes.number,
}

GeneBrowserTab.defaultProps = {
  fileId: null,
  analysisWrapperId: null,
  inlineHeight: 650,
}

export default GeneBrowserTab
